import {
  ConfirmOtpResponse,
  ErrorResponse,
  ForgetPasswordResponse,
  LoginResponse,
  OtpResponse,
  ResumeResponse,
  ServiceResponse,
  StatusResponse,
  ValidateOtpResponse,
  Version,
} from '@bzbs/react-api-client';
import React, { createContext, useContext, useRef, useState } from 'react';
import { LoginResponseHandler } from '../types';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAppLocaleContext } from './locale_provider';
import { useAnalyticsContext } from './analytics_provider';

export type AuthContextType = {
  isInitialized: boolean;
  versionData?: Version | null;
  isLoading: boolean;
  token: string | null;
  isLoadingToken: boolean;
  isLoggedIn: boolean;
  error: ErrorResponse | null;
  actions: {
    init(uuid: string, appId: string, deviceToken: string): void;
    setUUID(value: string): void;
    setAppId(value: string): void;
    setDeviceToken(value: string): void;
    setClientVersion(value: string): void;
    setOs(value: string): void;
    setPlatform(value: string): void;
    setError(value: unknown): void;
    setLoginResponseHandler(fn: LoginResponseHandler): void;
    clearData: () => void;
    loginWithUsernamePassword: (username: string, password: string) => Promise<ServiceResponse<LoginResponse>>;
    loginWithGoogle: (token: string) => Promise<ServiceResponse<LoginResponse>>;
    loginWithFacebook: (token: string) => Promise<ServiceResponse<LoginResponse>>;
    loginWithApple: (token: string, refreshToken: string) => Promise<ServiceResponse<LoginResponse>>;
    loginWithUUID: () => Promise<ServiceResponse<LoginResponse>>;
    logout: () => Promise<ServiceResponse<unknown>>;
    forgetPassword: (
      contact: string,
      type: 'email' | 'contact_number'
    ) => Promise<ServiceResponse<ForgetPasswordResponse>>;
    resetPassword: (
      contact: string,
      otp: string,
      refCode: string,
      password: string
    ) => Promise<ServiceResponse<StatusResponse>>;
    sendOtp: (contact: string) => Promise<ServiceResponse<OtpResponse>>;
    validateOtp: (
      otp: string,
      refCode: string,
      contact: string,
      channel: string
    ) => Promise<ServiceResponse<ValidateOtpResponse>>;
    confirmOtp: (otp: string, refCode: string, contact: string) => Promise<ServiceResponse<ConfirmOtpResponse>>;
    resume: (clientVersion: string) => Promise<ServiceResponse<ResumeResponse>>;
    version: (clientVersion: string) => Promise<ServiceResponse<Version>>;
  };
};

export const AuthContext = createContext({
  isInitialized: false,
  authData: null,
  versionData: null,
  isLoading: false,
  isLoggedIn: false,
  error: null,
  token: null,
  isLoadingToken: false,
  actions: {
    init: () => {},
    setUUID: () => {},
    setAppId: () => {},
    setDeviceToken: () => {},
    setClientVersion: () => {},
    setOs: () => {},
    setPlatform: () => {},
    setError: () => {},
    setLoginResponseHandler: () => {},
    clearData: () => {},
    loginWithUsernamePassword: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loginWithGoogle: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loginWithFacebook: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loginWithApple: async () => ({ type: 'client-error', message: 'Not implemented' }),
    loginWithUUID: async () => ({ type: 'client-error', message: 'Not implemented' }),
    logout: async () => ({ type: 'client-error', message: 'Not implemented' }),
    forgetPassword: async () => ({ type: 'client-error', message: 'Not implemented' }),
    resetPassword: async () => ({ type: 'client-error', message: 'Not implemented' }),
    sendOtp: async () => ({ type: 'client-error', message: 'Not implemented' }),
    validateOtp: async () => ({ type: 'client-error', message: 'Not implemented' }),
    confirmOtp: async () => ({ type: 'client-error', message: 'Not implemented' }),
    resume: async () => ({ type: 'client-error', message: 'Not implemented' }),
    version: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as AuthContextType);

export function useAuthContext() {
  return useContext(AuthContext);
}

type getTokenFunction = () => Promise<string | null>;
type setTokenFunction = (token: string) => void;
type removeTokenFunction = () => void;

export type TokenFunctions = {
  getToken: getTokenFunction;
  setToken: setTokenFunction;
  removeToken: removeTokenFunction;
};

export function AuthProvider({
  tokenFunctions,
  children,
}: {
  tokenFunctions: TokenFunctions;
  children: React.ReactNode;
}) {
  const [versionData, setVersionData] = useState<Version | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [loginResponseHandler, setLoginResponseHandler] = useState<LoginResponseHandler>(() => {});

  const { bzbsService } = useBuzzebeesServiceContext();
  const { localeId } = useAppLocaleContext();
  const { actions: analyticsActions } = useAnalyticsContext();
  const uuid = useRef<string>('');
  const appId = useRef<string>('');
  const deviceToken = useRef<string>('');
  const clientVersion = useRef<string>('');
  const os = useRef<string>('');
  const platform = useRef<string>('');

  const setUUID = (value: string) => {
    uuid.current = value;
  };

  const setAppId = (value: string) => {
    appId.current = value;
  };

  const setDeviceToken = (value: string) => {
    deviceToken.current = value;
  };

  const setClientVersion = (value: string) => {
    clientVersion.current = value;
  };

  const setOs = (value: string) => {
    os.current = value;
  };

  const setPlatform = (value: string) => {
    platform.current = value;
  };

  const init = (uuid: string, appId: string, deviceToken: string) => {
    setUUID(uuid);
    setAppId(appId);
    setDeviceToken(deviceToken);
    checkLoggedIn();
  };

  const checkLoggedIn = async () => {
    try {
      setIsLoadingToken(true);
      const savedToken = await tokenFunctions.getToken();
      setIsLoadingToken(false);
      if (savedToken) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
      setToken(savedToken);
    } catch (error) {
      setIsLoggedIn(false);
    }

    setIsInitialized(true);
  };

  const clearData = () => {
    setVersionData(null);
    setError(null);
    setIsLoading(false);
    setIsLoggedIn(false);
    setToken(null);
  };

  const login = async (
    loginFn: () => Promise<ServiceResponse<LoginResponse>>
  ): Promise<ServiceResponse<LoginResponse>> => {
    setIsLoading(true);
    const response = await loginFn();
    setIsLoading(false);

    if (response.type === 'success') {
      tokenFunctions.setToken(response.model.token);
      setToken(response.model.token);
      setIsLoggedIn(true);
      setError(null);
      loginResponseHandler(response.model);
      analyticsActions.trackEvent({
        action: 'on_click',
        name: 'login_success',
        category: 'login',
      });
    } else {
      setError(response);
    }

    return response;
  };

  const loginWithUsernamePassword = async (
    username: string,
    password: string
  ): Promise<ServiceResponse<LoginResponse>> => {
    const response = await login(() =>
      bzbsService.authApi.usernamePasswordLogin({
        username: username,
        password: password,
        appId: appId.current,
        uuid: uuid.current,
        deviceToken: deviceToken.current,
        clientVersion: clientVersion.current,
        os: os.current,
        platform: platform.current,
        deviceLocale: localeId.toString(),
        deviceNotificationEnabled: true,
      })
    );

    return response;
  };

  const loginWithGoogle = async (token: string): Promise<ServiceResponse<LoginResponse>> => {
    const response = await login(() =>
      bzbsService.authApi.googleLogin({
        idToken: token,
        appId: appId.current,
        uuid: uuid.current,
        deviceToken: deviceToken.current,
        clientVersion: clientVersion.current,
        os: os.current,
        platform: platform.current,
        deviceLocale: localeId.toString(),
        deviceNotificationEnabled: true,
      })
    );

    return response;
  };

  const loginWithFacebook = async (token: string): Promise<ServiceResponse<LoginResponse>> => {
    const response = await login(() =>
      bzbsService.authApi.facebookLogin({
        accessToken: token,
        appId: appId.current,
        uuid: uuid.current,
        deviceToken: deviceToken.current,
        clientVersion: clientVersion.current,
        os: os.current,
        platform: platform.current,
        deviceLocale: localeId.toString(),
        deviceNotificationEnabled: true,
      })
    );

    return response;
  };

  const loginWithApple = async (token: string, refreshToken: string): Promise<ServiceResponse<LoginResponse>> => {
    const response = await login(() =>
      bzbsService.authApi.appleLogin({
        idToken: token,
        refreshToken: refreshToken,
        appId: appId.current,
        uuid: uuid.current,
        deviceToken: deviceToken.current,
        clientVersion: clientVersion.current,
        os: os.current,
        platform: platform.current,
        deviceLocale: localeId.toString(),
        deviceNotificationEnabled: true,
      })
    );

    return response;
  };

  const loginWithUUID = async (): Promise<ServiceResponse<LoginResponse>> => {
    const response = await login(() =>
      bzbsService.authApi.deviceLogin({
        appId: appId.current,
        uuid: uuid.current,
        deviceToken: deviceToken.current,
        clientVersion: clientVersion.current,
        os: os.current,
        platform: platform.current,
        deviceLocale: localeId.toString(),
        deviceNotificationEnabled: true,
      })
    );

    return response;
  };

  const logout = async (): Promise<ServiceResponse<unknown>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.logout({ uuid: uuid.current });
    setIsLoading(false);
    tokenFunctions.removeToken();
    setToken(null);
    setIsLoggedIn(false);
    setError(null);
    return response;
  };

  const forgetPassword = async (
    contact: string,
    type: 'email' | 'contact_number'
  ): Promise<ServiceResponse<ForgetPasswordResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.forgetPassword({ contact: contact, type: type });
    setIsLoading(false);
    return response;
  };

  const resetPassword = async (
    contact: string,
    otp: string,
    refCode: string,
    newPassword: string
  ): Promise<ServiceResponse<StatusResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.resetPassword({
      contact: contact,
      refCode: refCode,
      otp: otp,
      newPassword: newPassword,
    });
    setIsLoading(false);
    return response;
  };

  const sendOtp = async (contact: string): Promise<ServiceResponse<OtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.otp({
      uuid: uuid.current,
      appId: appId.current,
      contactNumber: contact,
    });
    setIsLoading(false);
    return response;
  };

  const validateOtp = async (
    contact: string,
    otp: string,
    refCode: string,
    channel: string
  ): Promise<ServiceResponse<ValidateOtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.validateOtp({
      appId: appId.current,
      otp: otp,
      refCode: refCode,
      contactNumber: contact,
      use: false,
      channel: channel,
    });
    setIsLoading(false);
    return response;
  };

  const confirmOtp = async (
    contact: string,
    otp: string,
    refCode: string
  ): Promise<ServiceResponse<ConfirmOtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.confirmOtp({ otp: otp, refCode: refCode, contactNumber: contact });
    setIsLoading(false);
    return response;
  };

  const resume = async (clientVersion: string): Promise<ServiceResponse<ResumeResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.resume({
      uuid: uuid.current,
      appId: appId.current,
      clientVersion: clientVersion,
      deviceToken: deviceToken.current,
    });
    setIsLoading(false);
    return response;
  };

  const version = async (clientVersion: string): Promise<ServiceResponse<Version>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.version(clientVersion);
    if (response.type === 'success') {
      setVersionData(response.model);
    }
    setIsLoading(false);
    return response;
  };

  return (
    <AuthContext.Provider
      value={{
        isInitialized,
        versionData,
        isLoading,
        isLoggedIn,
        token,
        isLoadingToken,
        error,
        actions: {
          init,
          setUUID,
          setAppId,
          setDeviceToken,
          setClientVersion,
          setOs,
          setPlatform,
          setError,
          setLoginResponseHandler,
          clearData,
          loginWithUsernamePassword,
          loginWithGoogle,
          loginWithFacebook,
          loginWithApple,
          loginWithUUID,
          logout,
          forgetPassword,
          resetPassword,
          sendOtp,
          validateOtp,
          confirmOtp,
          resume,
          version,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
